import React, { useMemo } from 'react';
import classNames from 'classnames';
import styles from './ArticlesSectionComponent.module.scss';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';

import SliderNavCircle from '@/components/Functional/Slider/SliderNavCircle';
import ScLink from '@/components/Generic/SitecoreWrappers/ScLink';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { useI18n } from 'next-localization';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { GetBadgeValue } from 'components/Tiles/RelatedArticleTile';

type SliderCardComponentProps<T> = {
  parentBackground?: string;
  articleCard: T;
  baseUrl?: string;
  getBadgeValue?: GetBadgeValue;
};
type SliderCardComponent<T> = React.ComponentType<SliderCardComponentProps<T>>;

type ArticlesSectionComponentProps<T extends { id: string }> = {
  rendering: ComponentRendering<{ baseUrl?: Field<string> }>;
  articles: T[];
  sectionTitle?: string;
  navPrevSelector?: string;
  navNextSelector?: string;
  backgroundColor?: string;
  sliderCardComponent: SliderCardComponent<T>;
  getBadgeValue?: GetBadgeValue;
  heading?: React.ReactNode;
  viewAllSrc?: string;
  baseUrl?: string;
  className?: string;
};

const NEXT_CLASS = 'nav-next';
const PREV_CLASS = 'nav-prev';

const ArticlesSectionComponent = <T extends { id: string }>({
  articles,
  rendering,
  sectionTitle = '',
  navPrevSelector = `.${PREV_CLASS}`,
  navNextSelector = `.${NEXT_CLASS}`,
  backgroundColor = 'background-digital-light-grey',
  sliderCardComponent: SliderCardComponent,
  heading,
  viewAllSrc,
  getBadgeValue,
  baseUrl,
  className = '',
}: ArticlesSectionComponentProps<T>) => {
  const { t } = useI18n();

  const getBackgroundColor = rendering?.params?.background || backgroundColor;
  const isDefaultHeading = !heading;

  const scLinkField = viewAllSrc && {
    value: {
      href: viewAllSrc,
    },
  };

  const sliderOptions: SwiperProps = useMemo(() => {
    return {
      modules: [Navigation, Pagination, Keyboard],
      slidesPerView: 'auto',
      spaceBetween: 15,
      navigation: {
        nextEl: isDefaultHeading ? `.${NEXT_CLASS}` : navNextSelector,
        prevEl: isDefaultHeading ? `.${PREV_CLASS}` : navPrevSelector,
      },
      pagination: {
        el: `.slider-pagination`,
        type: 'bullets',
      },
      keyboard: {
        enabled: true,
      },
      breakpoints: {
        768: {
          spaceBetween: 30,
        },
      },
    };
  }, [isDefaultHeading, navNextSelector, navPrevSelector]);

  if (!articles?.length) return null;

  const defaultHeading = (
    <div
      className={classNames(
        styles.sliderHeading,
        'd-flex align-items-center justify-content-between mb-4 mb-md-5 background-to-change'
      )}
    >
      <h2 className="h4">{sectionTitle || 'Related Articles'}</h2>
      <div className="d-none d-xl-flex slider-navigation align-items-center">
        <SliderNavCircle aria-label="Previous slide" className={PREV_CLASS} direction="left" />
        <SliderNavCircle
          aria-label="Next slide"
          className={`ms-3 ${NEXT_CLASS}`}
          direction="right"
        />
      </div>
    </div>
  );

  return (
    <section
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      className={classNames(
        getBackgroundColor,
        className,
        'overflow-hidden default-section-padding'
      )}
    >
      <div className={classNames(styles.sliderOuter, 'container')}>
        <Swiper {...sliderOptions} className="overflow-visible">
          <div slot="container-start">{heading ?? defaultHeading}</div>
          {articles.map((articleCard) => (
            <SwiperSlide key={articleCard.id} className={styles.swiperSlide}>
              <SliderCardComponent
                parentBackground={getBackgroundColor}
                articleCard={articleCard}
                baseUrl={rendering.fields?.baseUrl?.value || baseUrl}
                getBadgeValue={getBadgeValue}
              />
            </SwiperSlide>
          ))}
          <div
            className={classNames(
              styles.sliderPagination,
              'd-xl-none slider-pagination d-flex align-items-center justify-content-center mt-4 mt-md-5'
            )}
          />
        </Swiper>

        {scLinkField && (
          <ScLink
            field={scLinkField}
            showExternalIcon={false}
            className={classNames(
              styles.viewAllCta,
              'background-to-change mt-4 hover-text-decoration-none'
            )}
          >
            {t('relatedArticlesViewAll')}
          </ScLink>
        )}
      </div>
    </section>
  );
};

export default ArticlesSectionComponent;
